import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle, Loader2, ChevronDown, ChevronRight, Globe } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { apiClient, TopologyIncident } from '../../api/client';
import {
  ReactFlow,
  Background,
  Controls,
  useNodesState,
  useEdgesState,
  Node,
  Edge,
  Handle,
  Position,
  MarkerType,
  ReactFlowProvider,
  useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { getWorkloadIcon } from '../TopologyMap/NodeComponents';

export interface ServiceDependencyGraphProps {
  clusterId: string;
  signals: any[];
  affectedResources: any[];
}

// Unified flow entry used for graph building — works for both L3/L4 and L7 data
interface UnifiedFlow {
  src_namespace: string;
  src_kind: string;
  src_name: string;
  dst_namespace: string;
  dst_kind: string;
  dst_name: string;
  count: number;
  allowed: boolean;
  src_known_ip_name?: string;
  dst_known_ip_name?: string;
}

// Clean L7 access log endpoint names: strip the redundant namespace suffix that
// Istio/Envoy appends to pod names in L7 logs.
function cleanL7EndpointName(name: string, kind: string, namespace: string): string {
  if (kind === 'Pod' && name.endsWith(`.${namespace}`)) {
    return name.slice(0, -(`.${namespace}`.length));
  }
  return name;
}

function mergeResponseToUnifiedFlows(response: { flows?: any[]; l7_access_logs?: any[] }): UnifiedFlow[] {
  const unified: UnifiedFlow[] = [];
  for (const f of (response.flows || [])) {
    if (!f.src_name && !f.dst_name) continue;
    unified.push({ src_namespace: f.src_namespace, src_kind: f.src_kind, src_name: f.src_name, dst_namespace: f.dst_namespace, dst_kind: f.dst_kind, dst_name: f.dst_name, count: f.count, allowed: f.allowed, src_known_ip_name: f.src_known_ip_name, dst_known_ip_name: f.dst_known_ip_name });
  }
  for (const l of (response.l7_access_logs || [])) {
    if (!l.src_name || !l.dst_name) continue;
    unified.push({ src_namespace: l.src_namespace, src_kind: l.src_kind, src_name: cleanL7EndpointName(l.src_name, l.src_kind, l.src_namespace), dst_namespace: l.dst_namespace, dst_kind: l.dst_kind, dst_name: cleanL7EndpointName(l.dst_name, l.dst_kind, l.dst_namespace), count: l.count, allowed: l.allowed });
  }
  return unified;
}

function matchesWorkloadName(flowName: string, flowNs: string, workloadName: string, workloadNs: string): boolean {
  if (flowNs !== workloadNs) return false;
  return flowName === workloadName || flowName.startsWith(workloadName + '-');
}

function resolveToWorkloadName(name: string, kind: string): { name: string; kind: string } {
  if (kind !== 'Pod') return { name, kind };
  const parts = name.split('-');
  if (parts.length >= 3) {
    const candidate = parts.slice(0, -2).join('-');
    if (candidate.length > 0) return { name: candidate, kind: 'Workload' };
  }
  return { name, kind: 'Pod' };
}

function workloadKey(ns: string, name: string): string {
  return `${ns}/${name}`;
}

interface NeighborInfo {
  name: string;
  namespace: string;
  kind: string;
  count: number;
}

function extractNeighbors(flows: UnifiedFlow[], workloadName: string, workloadNs: string): { sources: Map<string, NeighborInfo>; destinations: Map<string, NeighborInfo> } {
  const sources = new Map<string, NeighborInfo>();
  const destinations = new Map<string, NeighborInfo>();

  flows.forEach(flow => {
    const isSrcMatch = matchesWorkloadName(flow.src_name, flow.src_namespace, workloadName, workloadNs);
    const isDstMatch = matchesWorkloadName(flow.dst_name, flow.dst_namespace, workloadName, workloadNs);

    if (isSrcMatch && !isDstMatch) {
      const ns = flow.dst_namespace;
      const isExt = !ns || ns === '' || ns === 'external' || flow.dst_kind === 'External' || flow.dst_kind === 'Unmanaged';
      if (!isExt) {
        const resolved = resolveToWorkloadName(flow.dst_name, flow.dst_kind);
        const key = workloadKey(ns, resolved.name);
        const existing = destinations.get(key);
        const prevNs = existing?.namespace || '';
        const useNew = !existing || (prevNs === 'default' && ns !== 'default');
        const preferKind = !existing || (existing.kind === 'Pod' && resolved.kind !== 'Pod') || (existing.kind === 'Workload' && !['Pod', 'Workload'].includes(resolved.kind));
        destinations.set(key, { name: resolved.name, namespace: useNew ? ns : prevNs, kind: preferKind ? resolved.kind : (existing?.kind || resolved.kind), count: (existing?.count || 0) + (flow.count || 1) });
      }
    }
    if (isDstMatch && !isSrcMatch) {
      const ns = flow.src_namespace;
      const isExt = !ns || ns === '' || ns === 'external' || flow.src_kind === 'External' || flow.src_kind === 'Unmanaged';
      if (!isExt) {
        const resolved = resolveToWorkloadName(flow.src_name, flow.src_kind);
        const key = workloadKey(ns, resolved.name);
        const existing = sources.get(key);
        const prevNs = existing?.namespace || '';
        const useNew = !existing || (prevNs === 'default' && ns !== 'default');
        const preferKind = !existing || (existing.kind === 'Pod' && resolved.kind !== 'Pod') || (existing.kind === 'Workload' && !['Pod', 'Workload'].includes(resolved.kind));
        sources.set(key, { name: resolved.name, namespace: useNew ? ns : prevNs, kind: preferKind ? resolved.kind : (existing?.kind || resolved.kind), count: (existing?.count || 0) + (flow.count || 1) });
      }
    }
  });
  return { sources, destinations };
}

interface ExpandedNodeData {
  flows: UnifiedFlow[];
  sources: Map<string, NeighborInfo>;
  destinations: Map<string, NeighborInfo>;
  degree: number;
}

interface DependencyNodeData extends Record<string, unknown> {
  label: string;
  kind: string;
  namespace: string;
  isPrimary: boolean;
  isExternal: boolean;
  incidentCount: number;
  incidentSeverity: string;
  incidentId: string;
  isExpanded: boolean;
  isExpandable: boolean;
  isLoading: boolean;
  isBidirectional: boolean;
  direction: 'source' | 'destination' | 'primary';
}

const DependencyNode: React.FC<{ data: DependencyNodeData }> = ({ data }) => {
  const hasIncident = !data.isPrimary && data.incidentCount > 0;

  const getBorderStyles = () => {
    if (data.isPrimary) {
      return 'border-2 border-red-500 bg-red-50 dark:border-red-400 dark:bg-red-950/30 ring-2 ring-red-200 dark:ring-red-900/50';
    }
    if (data.isExpanded) {
      return 'border-2 border-red-500 bg-red-50/50 dark:border-red-400 dark:bg-red-950/20 ring-1 ring-red-200 dark:ring-red-900/30';
    }
    if (hasIncident) {
      return 'border-2 border-dashed animate-incident-border-pulse';
    }
    if (data.isExternal) {
      return 'border-2 border-orange-400 bg-orange-50 dark:border-orange-500 dark:bg-orange-950/20';
    }
    return 'border-2 border-purple-400 bg-purple-50 dark:border-purple-500 dark:bg-purple-950/20';
  };

  const getIcon = () => {
    if (data.isLoading) return <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />;
    if (data.isExternal) return <Globe className="h-5 w-5 text-orange-500" />;
    return getWorkloadIcon(data.kind, 'h-5 w-5');
  };

  const displayName = data.label.length > 22 ? data.label.substring(0, 22) + '...' : data.label;

  return (
    <div className="relative">
      <Handle type="target" position={Position.Left} className="!bg-muted-foreground !w-2 !h-2" />
      <Handle type="source" position={Position.Right} className="!bg-muted-foreground !w-2 !h-2" />
      <div className={cn(
        'rounded-lg p-3 shadow-sm transition-all duration-200 hover:shadow-md',
        'min-w-[140px] max-w-[180px]',
        (hasIncident || data.isExpandable) ? 'cursor-pointer' : 'cursor-default',
        getBorderStyles()
      )}>
        <div className="flex flex-col items-center gap-1.5 text-center">
          {getIcon()}
          <div className="leading-tight w-full">
            <h5 className="font-semibold text-xs text-foreground line-clamp-2" title={data.label}>
              {displayName}
            </h5>
            {!data.isExternal && data.namespace && (
              <p className="text-[10px] text-muted-foreground truncate">{data.namespace}</p>
            )}
            <p className="text-[10px] text-muted-foreground">{data.kind || (data.isExternal ? 'External' : 'Workload')}</p>
          </div>
          {data.isBidirectional && (
            <div className="text-[9px] text-emerald-500 font-medium">&#8596; bidirectional</div>
          )}
          {data.isPrimary && (
            <Badge variant="destructive" className="text-[9px] px-1.5 py-0">
              Affected
            </Badge>
          )}
          {hasIncident && (
            <Link
              to={`/incidents/${data.incidentId}`}
              onClick={(e) => e.stopPropagation()}
              className="no-underline"
            >
              <Badge variant="destructive" className="text-[9px] px-1.5 py-0 hover:bg-red-700 transition-colors">
                {data.incidentCount} incident{data.incidentCount !== 1 ? 's' : ''} &rarr;
              </Badge>
            </Link>
          )}
          {data.isExpandable && !data.isExpanded && !data.isLoading && (
            <div className="flex items-center gap-1 text-[9px] text-muted-foreground">
              <ChevronRight className="h-3 w-3" />
              <span>Expand deps</span>
            </div>
          )}
          {data.isExpanded && (
            <div className="flex items-center gap-1 text-[9px] text-indigo-500">
              <ChevronDown className="h-3 w-3" />
              <span>Collapse</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const dependencyNodeTypes = { dependency: DependencyNode };

function ServiceDependencyGraphInner({ clusterId, signals, affectedResources }: ServiceDependencyGraphProps) {
  const [flowData, setFlowData] = useState<UnifiedFlow[]>([]);
  const [neighborIncidents, setNeighborIncidents] = useState<Map<string, { count: number; severity: string; namespace: string; incidentId: string }>>(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedNodes, setExpandedNodes] = useState<Map<string, ExpandedNodeData>>(new Map());
  const [loadingNodes, setLoadingNodes] = useState<Set<string>>(new Set());
  const { fitView } = useReactFlow();
  const navigate = useNavigate();

  const primaryWorkload = useMemo(() => {
    const workloadTypes = ['Deployment', 'StatefulSet', 'DaemonSet', 'Job', 'CronJob'];
    const best = affectedResources.find((r: any) =>
      workloadTypes.includes(r.type || r.kind || r.resource_type)
    ) || affectedResources.find((r: any) =>
      (r.type || r.kind || r.resource_type) !== 'Node' && (r.type || r.kind || r.resource_type) !== 'Pod'
    );
    if (best?.name && best?.namespace) {
      return { name: best.name, namespace: best.namespace, kind: best.type || best.kind || best.resource_type || 'Workload' };
    }
    if (signals.length > 0 && signals[0].resource_name && signals[0].namespace) {
      const resourceName = signals[0].resource_name;
      const resourceKind = signals[0].resource_kind || '';
      if (resourceKind.toLowerCase() === 'pod') {
        const resolved = resolveToWorkloadName(resourceName, 'Pod');
        return { name: resolved.name, namespace: signals[0].namespace, kind: resolved.kind };
      }
      return { name: resourceName, namespace: signals[0].namespace, kind: resourceKind || 'Workload' };
    }
    return null;
  }, [affectedResources, signals]);

  // Fetch primary workload flows + neighbor incident data
  useEffect(() => {
    if (!primaryWorkload) { setIsLoading(false); return; }
    let cancelled = false;
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await apiClient.getWorkloadFlows({ cluster_id: clusterId, namespace: primaryWorkload.namespace, workload_name: primaryWorkload.name, limit: 500 });
        if (cancelled) return;
        const unified = mergeResponseToUnifiedFlows(response);
        setFlowData(unified);

        const neighborMap = new Map<string, string>();
        unified.forEach(flow => {
          const addIfNeighbor = (name: string, kind: string, ns: string) => {
            if (matchesWorkloadName(name, ns, primaryWorkload.name, primaryWorkload.namespace)) return;
            if (!ns || ns === '' || ns === 'external' || kind === 'External' || kind === 'Unmanaged') return;
            const resolved = resolveToWorkloadName(name, kind);
            const key = workloadKey(ns, resolved.name);
            const prev = neighborMap.get(key);
            if (!prev || (prev === 'default' && ns !== 'default')) neighborMap.set(key, ns);
          };
          addIfNeighbor(flow.src_name, flow.src_kind, flow.src_namespace);
          addIfNeighbor(flow.dst_name, flow.dst_kind, flow.dst_namespace);
        });

        const incidentMap = new Map<string, { count: number; severity: string; namespace: string; incidentId: string }>();
        const entries = Array.from(neighborMap.entries());
        const BATCH_SIZE = 5;
        for (let i = 0; i < entries.length; i += BATCH_SIZE) {
          const batch = entries.slice(i, i + BATCH_SIZE);
          await Promise.all(batch.map(async ([qualifiedKey, ns]) => {
            try {
              const bareName = qualifiedKey.split('/').slice(1).join('/');
              const res = await apiClient.getIncidentsByResource({ cluster_id: clusterId, resource_type: 'workload', namespace: ns, workload: bareName });
              if (res.count > 0 && res.incidents?.length > 0) {
                const highestSeverity = res.incidents.reduce((h: string, inc: TopologyIncident) => { const o = ['critical','high','medium','low']; return o.indexOf(inc.severity) < o.indexOf(h) ? inc.severity : h; }, 'low');
                incidentMap.set(qualifiedKey, { count: res.incidents.length, severity: highestSeverity, namespace: ns, incidentId: res.incidents[0].id });
              }
            } catch { /* ignore */ }
          }));
        }
        if (!cancelled) setNeighborIncidents(incidentMap);
      } catch (err: any) {
        if (!cancelled) setError(err.message || 'Failed to load service dependencies');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clusterId, primaryWorkload?.name, primaryWorkload?.namespace]);

  const toggleExpand = useCallback(async (workloadName: string, namespace: string, degree: number) => {
    const nodeKey = workloadKey(namespace, workloadName);
    if (expandedNodes.has(nodeKey)) {
      setExpandedNodes(prev => { const next = new Map(prev); next.delete(nodeKey); return next; });
      return;
    }
    setExpandedNodes(prev => {
      const next = new Map(prev);
      const toDelete: string[] = [];
      next.forEach((val, key) => { if (val.degree === degree) toDelete.push(key); });
      toDelete.forEach(key => { next.delete(key); });
      return next;
    });
    setLoadingNodes(prev => new Set(prev).add(nodeKey));
    try {
      const response = await apiClient.getWorkloadFlows({ cluster_id: clusterId, namespace, workload_name: workloadName, limit: 500 });
      const flows = mergeResponseToUnifiedFlows(response);
      const { sources, destinations } = extractNeighbors(flows, workloadName, namespace);
      setExpandedNodes(prev => { const next = new Map(prev); next.set(nodeKey, { flows, sources, destinations, degree }); return next; });
    } catch { /* ignore errors for expansion */ }
    setLoadingNodes(prev => { const next = new Set(prev); next.delete(nodeKey); return next; });
  }, [clusterId, expandedNodes]);

  const { nodes: graphNodes, edges: graphEdges } = useMemo(() => {
    if (!primaryWorkload || flowData.length === 0) return { nodes: [] as Node[], edges: [] as Edge[] };

    const { sources, destinations } = extractNeighbors(flowData, primaryWorkload.name, primaryWorkload.namespace);

    const bidirectionalKeys = new Set<string>();
    sources.forEach((_, key) => { if (destinations.has(key)) bidirectionalKeys.add(key); });

    const topSources = Array.from(sources.entries()).sort((a, b) => b[1].count - a[1].count).slice(0, 8);
    const topDests = Array.from(destinations.entries()).sort((a, b) => b[1].count - a[1].count).slice(0, 8);

    const nodes: Node[] = [];
    const edges: Edge[] = [];
    const edgeColor = '#10b981';
    const ySpacing = 160;
    const colWidth = 300;

    const expandedDstNames = new Set<string>();
    topDests.forEach(([key]) => { if (expandedNodes.has(key)) expandedDstNames.add(key); });
    const expandedSrcNames = new Set<string>();
    topSources.forEach(([key]) => { if (expandedNodes.has(key)) expandedSrcNames.add(key); });

    const srcX = 50;
    const primaryX = srcX + colWidth;
    const dstX = primaryX + colWidth;
    const expandedDstX = dstX + colWidth;
    const expandedSrcX = srcX - colWidth;

    const srcCount = topSources.length;
    const dstCount = topDests.length;

    let maxExpandedDstDeps = 0;
    let maxExpandedSrcDeps = 0;
    expandedDstNames.forEach(name => {
      const data = expandedNodes.get(name);
      if (data) maxExpandedDstDeps = Math.max(maxExpandedDstDeps, data.destinations.size);
    });
    expandedSrcNames.forEach(name => {
      const data = expandedNodes.get(name);
      if (data) maxExpandedSrcDeps = Math.max(maxExpandedSrcDeps, data.sources.size);
    });

    const maxSide = Math.max(srcCount, dstCount, maxExpandedDstDeps, maxExpandedSrcDeps, 1);
    const totalHeight = (maxSide - 1) * ySpacing;
    const centerY = totalHeight / 2;

    const makeNodeData = (label: string, kind: string, ns: string, isPrimary: boolean, dir: string, incData?: { count: number; severity: string; incidentId: string }, isExpanded = false, isExpandable = false, isNodeLoading = false, isBidir = false): DependencyNodeData => ({
      label, kind, namespace: ns, isPrimary, isExternal: false,
      incidentCount: incData?.count || 0, incidentSeverity: incData?.severity || '', incidentId: incData?.incidentId || '',
      isExpanded, isExpandable, isLoading: isNodeLoading, isBidirectional: isBidir,
      direction: dir as any,
    });

    nodes.push({ id: 'primary', type: 'dependency', position: { x: primaryX, y: centerY }, data: makeNodeData(primaryWorkload.name, primaryWorkload.kind, primaryWorkload.namespace, true, 'primary') });

    const srcOff = centerY - ((srcCount - 1) * ySpacing) / 2;
    topSources.forEach(([key, info], idx) => {
      const incData = neighborIncidents.get(key);
      const isExp = expandedNodes.has(key);
      const isExpandable = !!incData && incData.count > 0;
      const isNodeLoading = loadingNodes.has(key);
      const isBidir = bidirectionalKeys.has(key);
      nodes.push({ id: `src-${key}`, type: 'dependency', position: { x: srcX, y: srcOff + idx * ySpacing }, data: makeNodeData(info.name, info.kind, info.namespace, false, 'source', incData, isExp, isExpandable, isNodeLoading, isBidir) });
      edges.push({ id: `edge-src-${key}`, source: `src-${key}`, target: 'primary', type: 'default', animated: true, style: { stroke: edgeColor, strokeWidth: 2 }, markerEnd: { type: MarkerType.ArrowClosed, color: edgeColor } });

      if (isExp) {
        const parentY = srcOff + idx * ySpacing;
        const expData = expandedNodes.get(key)!;
        const primaryKey = workloadKey(primaryWorkload.namespace, primaryWorkload.name);
        const expSources = Array.from(expData.sources.entries()).filter(([n]) => n !== primaryKey).sort((a, b) => b[1].count - a[1].count).slice(0, 6);
        const expOff = parentY - ((expSources.length - 1) * ySpacing) / 2;
        expSources.forEach(([depKey, depInfo], depIdx) => {
          const depId = `exp-src-${key}-${depKey}`;
          const depIncData = neighborIncidents.get(depKey);
          const depIsExp = expandedNodes.has(depKey);
          const depIsExpandable = !!depIncData && depIncData.count > 0;
          const depIsLoading = loadingNodes.has(depKey);
          nodes.push({ id: depId, type: 'dependency', position: { x: expandedSrcX, y: expOff + depIdx * ySpacing }, data: makeNodeData(depInfo.name, depInfo.kind, depInfo.namespace, false, 'source', depIncData, depIsExp, depIsExpandable, depIsLoading) });
          edges.push({ id: `edge-${depId}`, source: depId, target: `src-${key}`, type: 'default', animated: true, style: { stroke: edgeColor, strokeWidth: 2 }, markerEnd: { type: MarkerType.ArrowClosed, color: edgeColor } });
        });
      }
    });

    const dstOff = centerY - ((dstCount - 1) * ySpacing) / 2;
    topDests.forEach(([key, info], idx) => {
      const incData = neighborIncidents.get(key);
      const isExp = expandedNodes.has(key);
      const isExpandable = !!incData && incData.count > 0;
      const isNodeLoading = loadingNodes.has(key);
      const isBidir = bidirectionalKeys.has(key);
      nodes.push({ id: `dst-${key}`, type: 'dependency', position: { x: dstX, y: dstOff + idx * ySpacing }, data: makeNodeData(info.name, info.kind, info.namespace, false, 'destination', incData, isExp, isExpandable, isNodeLoading, isBidir) });
      edges.push({ id: `edge-dst-${key}`, source: 'primary', target: `dst-${key}`, type: 'default', animated: true, style: { stroke: edgeColor, strokeWidth: 2 }, markerEnd: { type: MarkerType.ArrowClosed, color: edgeColor } });

      if (isExp) {
        const parentY = dstOff + idx * ySpacing;
        const expData = expandedNodes.get(key)!;
        const primaryKey = workloadKey(primaryWorkload.namespace, primaryWorkload.name);
        const expDests = Array.from(expData.destinations.entries()).filter(([n]) => n !== primaryKey).sort((a, b) => b[1].count - a[1].count).slice(0, 6);
        const expOff = parentY - ((expDests.length - 1) * ySpacing) / 2;
        expDests.forEach(([depKey, depInfo], depIdx) => {
          const depId = `exp-dst-${key}-${depKey}`;
          const depIncData = neighborIncidents.get(depKey);
          const depIsExp = expandedNodes.has(depKey);
          const depIsExpandable = !!depIncData && depIncData.count > 0;
          const depIsLoading = loadingNodes.has(depKey);
          nodes.push({ id: depId, type: 'dependency', position: { x: expandedDstX, y: expOff + depIdx * ySpacing }, data: makeNodeData(depInfo.name, depInfo.kind, depInfo.namespace, false, 'destination', depIncData, depIsExp, depIsExpandable, depIsLoading) });
          edges.push({ id: `edge-${depId}`, source: `dst-${key}`, target: depId, type: 'default', animated: true, style: { stroke: edgeColor, strokeWidth: 2 }, markerEnd: { type: MarkerType.ArrowClosed, color: edgeColor } });
        });
      }
    });

    return { nodes, edges };
  }, [primaryWorkload, flowData, neighborIncidents, expandedNodes, loadingNodes]);

  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);

  useEffect(() => {
    setNodes(graphNodes);
    setEdges(graphEdges);
    if (graphNodes.length > 0) {
      const timer = setTimeout(() => fitView({ padding: 0.2, duration: 300 }), 50);
      return () => clearTimeout(timer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graphNodes, graphEdges]);

  const onNodeClick = useCallback((_event: React.MouseEvent, node: Node) => {
    const data = node.data as DependencyNodeData;
    if (data.isPrimary) return;
    if (data.isExpandable || data.isExpanded) {
      const degree = node.id.startsWith('exp-') ? 2 : 1;
      toggleExpand(data.label, data.namespace, degree);
    } else if (data.incidentId && data.incidentCount > 0) {
      navigate(`/incidents/${data.incidentId}`);
    }
  }, [navigate, toggleExpand]);

  if (!primaryWorkload || isLoading || error || graphNodes.length <= 1) return null;

  const incidentNeighbors = Array.from(neighborIncidents.entries());

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          Service Dependencies
        </CardTitle>
        <CardDescription>
          Services communicating with {primaryWorkload.namespace}/{primaryWorkload.name}
          {incidentNeighbors.length > 0 && (
            <span className="text-red-500 font-medium ml-1">
              &mdash; {incidentNeighbors.length} dependent service{incidentNeighbors.length !== 1 ? 's' : ''} also experiencing incidents
            </span>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        <div className="h-[400px] w-full border-t">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onNodeClick={onNodeClick}
            nodeTypes={dependencyNodeTypes}
            fitView
            fitViewOptions={{ padding: 0.3 }}
            minZoom={0.3}
            maxZoom={1.5}
            proOptions={{ hideAttribution: true }}
            nodesDraggable={false}
            nodesConnectable={false}
            elementsSelectable={true}
          >
            <Background />
            <Controls showInteractive={false} />
          </ReactFlow>
        </div>
        {incidentNeighbors.length > 0 && (
          <div className="px-4 py-3 border-t bg-red-50/50 dark:bg-red-950/10">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="h-4 w-4 text-red-500" />
              <span className="text-sm font-medium text-red-700 dark:text-red-300">
                Related Incidents on Dependent Services
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {incidentNeighbors.map(([key, data]) => (
                <Badge
                  key={key}
                  variant="outline"
                  className={cn(
                    'text-xs',
                    data.severity === 'critical' && 'border-red-300 text-red-700 dark:text-red-300',
                    data.severity === 'high' && 'border-orange-300 text-orange-700 dark:text-orange-300',
                    data.severity === 'medium' && 'border-yellow-300 text-yellow-700 dark:text-yellow-300',
                    data.severity === 'low' && 'border-blue-300 text-blue-700 dark:text-blue-300',
                  )}
                >
                  {data.namespace}/{key} ({data.count} incident{data.count !== 1 ? 's' : ''})
                </Badge>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function ServiceDependencyGraph(props: ServiceDependencyGraphProps) {
  return (
    <ReactFlowProvider>
      <ServiceDependencyGraphInner {...props} />
    </ReactFlowProvider>
  );
}


